import type { MeterUploadRowData } from "./buildTrialRunRow";

// A raw subscriber record from GET /admin/subscribers (scheme-scoped via the
// level1ResourceId query param; ordered by name server-side). The endpoint returns
// the full Subscriber model — only the fields the account cell needs are typed here.
export type SubscriberLookupRecord = {
  id: string;
  name: string;
  accountNumber: string;
};

// A name-first option for the account-cell select. `value` is the account number
// (the field the row stores), so a selection maps straight onto rowData.accountNumber
// and there is no id-to-account indirection to keep in sync.
export type SubscriberOption = {
  // "<name> (<accountNumber>)" — the customer name leads so the operator picks by name.
  label: string;
  value: string;
  name: string;
  accountNumber: string;
};

export const subscriberOptionLabel = (
  name: string,
  accountNumber: string,
): string => (name ? `${name} (${accountNumber})` : accountNumber);

// Map subscriber records to name-first options, skipping any without an account
// number (the cell cannot fill the row without one) and de-duplicating by account
// number (keep the first). Tolerates an undefined list while the lookup loads.
export const toSubscriberOptions = (
  subscribers: SubscriberLookupRecord[] | undefined,
): SubscriberOption[] => {
  const seen = new Set<string>();
  const options: SubscriberOption[] = [];
  for (const subscriber of subscribers ?? []) {
    const accountNumber = subscriber?.accountNumber ?? "";
    if (!accountNumber || seen.has(accountNumber)) continue;
    seen.add(accountNumber);
    const name = subscriber.name ?? "";
    options.push({
      label: subscriberOptionLabel(name, accountNumber),
      value: accountNumber,
      name,
      accountNumber,
    });
  }
  return options;
};

// The option representing the row's current account number. A parsed/draft value
// with no matching subscriber still renders as the current value — a synthetic
// option carrying the raw account number — so it shows and can be replaced; an empty
// value selects nothing.
export const resolveCurrentOption = (
  accountNumber: string | undefined,
  options: SubscriberOption[],
): SubscriberOption | null => {
  if (!accountNumber) return null;
  const match = options.find(o => o.value === accountNumber);
  if (match) return match;
  return {
    label: accountNumber,
    value: accountNumber,
    name: "",
    accountNumber,
  };
};

// Apply a selection to the row: selecting a subscriber sets the account number,
// clearing the select empties it. The trial run — not this cell — is the source of
// validation truth, so no extra validation happens here.
export const commitSubscriberSelection = (
  rowData: MeterUploadRowData,
  option: SubscriberOption | null,
): MeterUploadRowData => ({
  ...rowData,
  accountNumber: option?.accountNumber ?? "",
});
